import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { withRouter } from 'react-router-dom'

// actions
import { useFundQuery, GET_FUNDS } from '../../hook'
import { openModal, modalType } from '../../../../actions/ui'
import { useExport } from '../../../../services/csvExport/csvExport.service'

// components
import FundToolbar from './toolbar/toolbar.component'
import FundTable from './table/table.component'
import {
  formatDate,
  getPageSizeFromStorage,
  getActiveTicker,
  isPassive,
  EXPORT_DATE_FORMAT,
  DEFAULT_TICKER
} from '../../../../utils'
import { capitalize, get, isEmpty, isNull, omitBy } from 'lodash'
import { getLocalizedCurrency } from '../../../../utils/currencyLocalization'

const propTypes = {
  dataIdPrefix: PropTypes.string,
  toolbarTheme: PropTypes.string,
  toolTheme: PropTypes.string,
  id: PropTypes.array,
  entityId: PropTypes.string
}

const defaultProps = {}

const PAGE_SIZE_ID = 'fund-grid'
const EMPTY_PLACEHOLDER = '-'

/**
 * Fund Grid Component
 * @param props
 */
function FundGrid (props) {
  const { dataIdPrefix, toolbarTheme, toolTheme, id, entityId, tickerId, securityId, history, openModal } = props
  const [state, setState] = useState({
    search: null,
    searchField: ['fundName', 'countryName', 'style', 'turnover', 'contactConnection.fullName'],
    filter: {
      all: true,
      holder: false,
      peerHolder: false
    },
    listOptions: {
      sortBy: 'fundHoldingCurrentConnection.current',
      page: 1,
      limit: getPageSizeFromStorage(PAGE_SIZE_ID) || 10
    }
  })
  const { filter, search, searchField, listOptions } = state
  const { holder, peerHolder } = filter
  const variables = omitBy({
    id,
    entityId,
    tickerId,
    securityId,
    holder,
    peerHolder,
    search,
    searchField,
    currencyCode: getLocalizedCurrency(),
    ...listOptions
  }, isNull)

  const { data, loading, client } = useFundQuery({
    skip: (!id || !id.length) && !entityId,
    variables
  })
  const { generateExport, exporting } = useExport({
    onError: () => {
      openModal({
        type: modalType.ERROR_MODAL
      })
    }
  })

  const funds = get(data, 'fund.items', [])
  const total = get(data, 'fund.count', 0)

  /**
   * Handle query change
   * @param query
   */
  const handleQueryChange = (query = {}) => {
    const options = query.listOptions ? { ...query } : { ...query, listOptions: { ...listOptions, page: 1 } }
    setState({ ...state, ...options })
  }

  /**
   * Format mapper for CSV export
   */
  const fundCSVMapper = (fund) => {
    const { fundName, institutionName, portfolioValue, style, turnover, equityAUM, qualityRating } = fund
    const contacts = get(fund, 'contactConnection.items', []).map((contact) => contact && contact.fullName)
    const aiScore = get(fund, 'aiScoreConnection.items[0].score', null)
    const reportDate = get(fund, 'fundHoldingCurrentConnection.items[0].reportDate', null)
    const activity = get(fund, 'activityConnection.items[0].start.date', null)

    return {
      'Fund Name': fundName || EMPTY_PLACEHOLDER,
      Pos: get(fund, 'fundHoldingCurrentConnection.items[0].current', EMPTY_PLACEHOLDER),
      'Institution Name': institutionName || EMPTY_PLACEHOLDER,
      Contacts: !isEmpty(contacts) ? contacts : EMPTY_PLACEHOLDER,
      Style: style || EMPTY_PLACEHOLDER,
      Turnover: turnover ? capitalize(turnover) : EMPTY_PLACEHOLDER,
      [`AUM (${getLocalizedCurrency()})`]: portfolioValue || EMPTY_PLACEHOLDER,
      [`EAUM (${getLocalizedCurrency()})`]: equityAUM || EMPTY_PLACEHOLDER,
      'As Of': reportDate ? formatDate(reportDate, EXPORT_DATE_FORMAT, true, true) : EMPTY_PLACEHOLDER,
      'Score': isPassive({ style, aiScore }),
      'Purchasing Power': get(fund, 'purchasingPowerConnection.items[0].purchasingPower', EMPTY_PLACEHOLDER),
      QR: qualityRating || EMPTY_PLACEHOLDER,
      'Last Activity': activity ? formatDate(activity, EXPORT_DATE_FORMAT, undefined, true) : EMPTY_PLACEHOLDER
    }
  }

  /**
   * Handle export
   */
  const handleExport = () => {
    const params = {
      client,
      variables: { ...variables, limit: 0 },
      query: GET_FUNDS,
      dataPath: 'data.fund.items',
      fileName: 'funds.csv',
      formatter: fundCSVMapper
    }

    generateExport(params)
  }

  return (
    <>
      <FundToolbar
        dataId={`${dataIdPrefix}Fund`}
        toolbarTheme={toolbarTheme}
        toolTheme={toolTheme}
        search={search}
        filter={filter}
        noData={!funds || !funds.length}
        onQueryChange={handleQueryChange}
        onExport={handleExport}
        exporting={exporting}
      />
      <FundTable
        pageSizeId={PAGE_SIZE_ID}
        loading={loading}
        data={funds}
        total={total}
        listOptions={listOptions}
        onQueryChange={handleQueryChange}
        history={history}
      />
    </>
  )
}

FundGrid.propTypes = propTypes
FundGrid.defaultProps = defaultProps

const mapStateToProps = (state) => {
  const ticker = getActiveTicker(get(state, 'profile.data'))

  return {
    tickerId: get(ticker, 'q4_ticker_id', DEFAULT_TICKER),
    securityId: get(ticker, 'q4_entity_id', '1234567890') // TODO: tmp default value for pre-IPO
  }
}

const mapDispatchToProps = (dispatch) => ({
  openModal: bindActionCreators(openModal, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FundGrid))
